import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ButtonSpinner } from '../../components/ui/Loader';
import {
  useGetUserDetailsQuery,
  useToggleUserBanMutation,
  useUpdateUserRoleMutation,
  useChangePasswordMutation,
  useDeleteUserMutation,
  useAdjustMainBalanceMutation,
  useAdjustReferralBalanceMutation,
  useAdjustCashbackBalanceMutation,
  useGetTransactionsQuery,
  useGetAdminNumbersQuery,
  useGetAdminProxiesQuery,
} from '../../store/api/adminApi';
import { useLanguage } from '../../hooks/useLanguage';
import { formatDate } from '../../utils/dateFormatter';
import { Link } from 'react-router-dom';

/** MongoDB ObjectId is 24 hex characters; use for _id. Otherwise treat as telegramId. */
function isMongoId(value: string | undefined): value is string {
  return typeof value === 'string' && /^[a-fA-F0-9]{24}$/.test(value);
}

export function AdminUserDetailsPage() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { userId: userIdParam } = useParams<{ userId: string }>();

  const userDetailsParams = (() => {
    if (!userIdParam) return undefined;
    if (isMongoId(userIdParam)) return { userId: userIdParam };
    const asNum = Number(userIdParam);
    if (Number.isInteger(asNum) && String(asNum) === userIdParam) return { telegramId: asNum };
    return { userId: userIdParam };
  })();

  const { data, isLoading, error, refetch } = useGetUserDetailsQuery(userDetailsParams!, {
    skip: !userDetailsParams,
  });

  const { data: transactionsData } = useGetTransactionsQuery(
    { userId: userIdParam ?? '', limit: 5, page: 1, sortBy: 'createdAt', sortOrder: -1 },
    { skip: !userIdParam }
  );
  const { data: userNumbersData } = useGetAdminNumbersQuery(
    {
      userId: userIdParam ?? '',
      page: 1,
      limit: 6,
      sortBy: 'purchasedAt',
      sortOrder: -1,
    },
    { skip: !userIdParam }
  );
  const { data: userProxiesData } = useGetAdminProxiesQuery(
    {
      userId: userIdParam ?? '',
      page: 1,
      limit: 6,
      sortBy: 'purchasedAt',
      sortOrder: -1,
    },
    { skip: !userIdParam }
  );

  const [toggleBan, { isLoading: isTogglingBan }] = useToggleUserBanMutation();
  const [updateRole, { isLoading: isUpdatingRole }] = useUpdateUserRoleMutation();
  const [changePassword, { isLoading: isChangingPassword }] = useChangePasswordMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const [adjustMainBalance, { isLoading: isAdjustingMain }] = useAdjustMainBalanceMutation();
  const [adjustReferralBalance, { isLoading: isAdjustingReferral }] = useAdjustReferralBalanceMutation();
  const [adjustCashbackBalance, { isLoading: isAdjustingCashback }] = useAdjustCashbackBalanceMutation();

  // Dialog states
  const [banDialog, setBanDialog] = useState(false);
  const [roleDialog, setRoleDialog] = useState(false);
  const [supportDialog, setSupportDialog] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [deleteDialog, setDeleteDialog] = useState(false);
  
  // Balance adjustment states
  const [balanceDialog, setBalanceDialog] = useState<{
    isOpen: boolean;
    type: 'main' | 'referral' | 'cashback';
    action: 'add' | 'deduct';
  }>({ isOpen: false, type: 'main', action: 'add' });
  
  const [balanceForm, setBalanceForm] = useState({
    amount: '',
    reason: '',
  });

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (error || !data?.data.user) {
    return (
      <div className="min-h-screen">
        <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-8 text-center">
          <h2 className="text-2xl font-heading font-bold text-danger mb-4">
            {t('admin.users.details.error.title')}
          </h2>
          <p className="text-neutral-medium font-body mb-6">
            {'data' in (error as { data?: { message?: string } })
              ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
              : t('admin.users.details.error.notFound')}
          </p>
          <button
            onClick={() => navigate('/admin/users')}
            className="px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
          >
            {t('admin.users.details.back')}
          </button>
        </div>
      </div>
    );
  }

  const user = data.data.user;
  const latestNumbers = userNumbersData?.data.numbers ?? [];
  const latestProxies = userProxiesData?.data.proxies ?? [];

  const handleBan = async () => {
    try {
      await toggleBan({
        userId: user._id,
        isBanned: !user.isBanned,
      }).unwrap();
      toast.success(
        user.isBanned
          ? t('admin.users.unbanSuccess')
          : t('admin.users.banSuccess')
      );
      setBanDialog(false);
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.users.banError')
      );
    }
  };

  const handleRole = async () => {
    try {
      await updateRole({
        userId: user._id,
        isAdmin: !user.isAdmin,
      }).unwrap();
      toast.success(t('admin.users.roleSuccess'));
      setRoleDialog(false);
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.users.roleError')
      );
    }
  };

  const handleSupport = async () => {
    try {
      await updateRole({
        userId: user._id,
        role: user.role === 'admin_support' ? 'user' : 'admin_support',
      }).unwrap();
      toast.success(t('admin.users.roleSuccess'));
      setSupportDialog(false);
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.users.roleError')
      );
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.newPassword || passwordForm.newPassword.length < 8) {
      toast.error(t('admin.users.password.minLength'));
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t('admin.users.password.mismatch'));
      return;
    }
    try {
      await changePassword({
        userId: user._id,
        newPassword: passwordForm.newPassword,
      }).unwrap();
      toast.success(t('admin.users.password.success'));
      setPasswordDialog(false);
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(error?.data?.message || error?.message || t('admin.users.password.error'));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteUser({ userId: user._id }).unwrap();
      toast.success(t('admin.users.deleteSuccess'));
      navigate('/admin/users');
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.users.deleteError')
      );
    }
  };

  return (
    <div className="min-h-screen animate-slide-up">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
            {t('admin.users.details.title')}
          </h1>
          <p className="text-neutral-medium text-lg font-body">
            {user.firstName} {user.lastName || ''}
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/users')}
          className="px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300"
        >
          {t('admin.users.details.back')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* User Information */}
          <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent mb-6">
              {t('admin.users.details.info.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-neutral-medium font-body mb-1 block">
                  {t('admin.users.details.info.firstName')}
                </label>
                <p className="text-neutral-light font-heading font-semibold">
                  {user.firstName}
                </p>
              </div>
              {user.lastName && (
                <div>
                  <label className="text-sm text-neutral-medium font-body mb-1 block">
                    {t('admin.users.details.info.lastName')}
                  </label>
                  <p className="text-neutral-light font-heading font-semibold">
                    {user.lastName}
                  </p>
                </div>
              )}
              {user.username && (
                <div>
                  <label className="text-sm text-neutral-medium font-body mb-1 block">
                    {t('admin.users.details.info.username')}
                  </label>
                  <p className="text-neutral-light font-heading font-semibold">
                    @{user.username}
                  </p>
                </div>
              )}
              {user.email && (
                <div>
                  <label className="text-sm text-neutral-medium font-body mb-1 block">
                    {t('admin.users.details.info.email')}
                  </label>
                  <p className="text-neutral-light font-heading font-semibold">
                    {user.email}
                  </p>
                </div>
              )}
              {user.telegramId && (
                <div>
                  <label className="text-sm text-neutral-medium font-body mb-1 block">
                    {t('admin.users.details.info.telegramId')}
                  </label>
                  <p className="text-neutral-light font-heading font-semibold">
                    {user.telegramId}
                  </p>
                </div>
              )}
              {!user.telegramId && user.legacyTelegramId && (
                <div>
                  <label className="text-sm text-neutral-medium font-body mb-1 block">
                    {t('admin.users.details.info.telegramId')}
                  </label>
                  <p className="text-neutral-light font-heading font-semibold flex items-center gap-2">
                    {user.legacyTelegramId}
                    <span className="text-xs bg-amber-900/40 text-amber-400 border border-amber-700/50 px-2 py-0.5 rounded-full font-heading">
                      {t('admin.users.details.info.migrated')}
                    </span>
                  </p>
                </div>
              )}
              <div>
                <label className="text-sm text-neutral-medium font-body mb-1 block">
                  {t('admin.users.details.info.authMethod')}
                </label>
                <p className="text-neutral-light font-heading font-semibold capitalize">
                  {user.authMethod}
                </p>
              </div>
              <div>
                <label className="text-sm text-neutral-medium font-body mb-1 block">
                  {t('admin.users.details.info.language')}
                </label>
                <p className="text-neutral-light font-heading font-semibold uppercase">
                  {user.language}
                </p>
              </div>
              <div>
                <label className="text-sm text-neutral-medium font-body mb-1 block">
                  {t('admin.users.details.info.createdAt')}
                </label>
                <p className="text-neutral-light font-heading font-semibold">
                  {formatDate(user.createdAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus')}
                </p>
              </div>
              <div>
                <label className="text-sm text-neutral-medium font-body mb-1 block">
                  {t('admin.users.details.info.updatedAt')}
                </label>
                <p className="text-neutral-light font-heading font-semibold">
                  {formatDate(user.updatedAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus')}
                </p>
              </div>
            </div>
          </div>

          {/* كروت أرقام المستخدم، بروكسياته، ومعاملاته — تحت كرت معلومات المستخدم (تحت بعض) */}
          <div className="space-y-6">
            {/* أرقام المستخدم */}
            <div className="card-glass rounded-2xl shadow-2xl border border-neutral-dark p-6">
              <h2 className="text-xl font-heading font-bold text-brand-accent mb-4">
                {t('admin.users.details.cards.numbers')}
              </h2>
              {latestNumbers.length > 0 ? (
                <ul className="space-y-2 mb-4">
                  {latestNumbers.map((num) => (
                    <li key={num._id} className="py-2 px-3 rounded-lg hover:bg-neutral-dark/50 transition-colors">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`text-xs font-heading px-2 py-0.5 rounded-full shrink-0 ${
                            num.numberType === 'permanent'
                              ? 'bg-green-900/40 text-green-400 border border-green-700/50'
                              : 'bg-blue-900/40 text-blue-400 border border-blue-700/50'
                          }`}>
                            {num.numberType === 'permanent' ? t('admin.numbers.type.permanent') : t('admin.numbers.type.temporary')}
                          </span>
                          <span className="text-neutral-light font-body break-all">{num.number}</span>
                        </div>
                        <span className="text-xs text-brand-accent font-heading">
                          {typeof num.price === 'number' ? `${num.price.toFixed(2)} USD` : '-'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-neutral-medium font-body">
                          {num.serviceName || '-'} - {num.countryName || '-'}
                        </span>
                        <span className="text-xs text-neutral-medium font-body">
                            {num.purchasedAt ? formatDate(num.purchasedAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus') : '-'}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-neutral-medium font-body text-sm mb-4">
                  {t('admin.users.details.cards.noNumbers')}
                </p>
              )}
              <Link
                to={`/admin/numbers?userId=${encodeURIComponent(user._id)}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
              >
                {t('admin.users.details.cards.viewAllNumbers')}
              </Link>
            </div>

            {/* بروكسيات المستخدم */}
            <div className="card-glass rounded-2xl shadow-2xl border border-neutral-dark p-6">
              <h2 className="text-xl font-heading font-bold text-brand-accent mb-4">
                {t('admin.users.details.cards.proxies')}
              </h2>
              {latestProxies.length > 0 ? (
                <ul className="space-y-2 mb-4">
                  {latestProxies.map((proxy) => (
                    <li key={proxy._id} className="py-2 px-3 rounded-lg hover:bg-neutral-dark/50 transition-colors">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-neutral-light font-body break-all">
                          {proxy.ipAddress}:{proxy.port}
                        </span>
                        <span className="text-xs text-brand-accent font-heading">
                          {typeof proxy.price === 'number' ? `${proxy.price.toFixed(2)} USD` : '-'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-neutral-medium font-body">
                          {proxy.serverId === 1 ? 'ISP Proxies' : proxy.serverId === 2 ? 'Datacenter Proxies' : proxy.serverId === 3 ? 'Private Residential Proxies' : proxy.serverId === 4 ? 'Rotating Mobile Proxy 🔥' : `Server ${proxy.serverId}`} - {proxy.type || '-'}
                        </span>
                        <span className="text-xs text-neutral-medium font-body">
                          {proxy.purchasedAt ? formatDate(proxy.purchasedAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus') : '-'}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-neutral-medium font-body text-sm mb-4">
                  {t('admin.users.details.cards.noProxies')}
                </p>
              )}
              <Link
                to={`/admin/proxies?userId=${encodeURIComponent(user._id)}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
              >
                {t('admin.users.details.cards.viewAllProxies')}
              </Link>
            </div>

            {/* معاملات المستخدم */}
            <div className="card-glass rounded-2xl shadow-2xl border border-neutral-dark p-6">
              <h2 className="text-xl font-heading font-bold text-brand-accent mb-4">
                {t('admin.users.details.cards.transactions')}
              </h2>
              {transactionsData?.data?.transactions?.length ? (
                <>
                  <ul className="space-y-2 mb-4">
                    {transactionsData.data.transactions.slice(0, 5).map((tx) => (
                      <li key={tx._id}>
                        <Link
                          to={`/admin/transactions?userId=${encodeURIComponent(user._id)}`}
                          className="flex justify-between items-center py-2 px-3 rounded-lg hover:bg-neutral-dark/50 transition-colors text-sm font-body"
                        >
                          <span className="text-neutral-light">
                            {t(`admin.transactions.types.${tx.type}`, { defaultValue: tx.type })}
                          </span>
                          <span className={tx.amount >= 0 ? 'text-green-500' : 'text-red-500'}>
                            {tx.amount >= 0 ? '+' : ''}{tx.amount.toFixed(2)} {tx.currency}
                          </span>
                        </Link>
                        <p className="text-xs text-neutral-medium font-body px-3">
                          {formatDate(tx.createdAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus')}
                        </p>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/admin/transactions?userId=${encodeURIComponent(user._id)}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
                  >
                    {t('admin.users.details.cards.viewAllTransactions')}
                  </Link>
                </>
              ) : (
                <>
                  <p className="text-neutral-medium font-body text-sm mb-4">
                    {t('admin.users.details.cards.noTransactions')}
                  </p>
                  <Link
                    to={`/admin/transactions?userId=${encodeURIComponent(user._id)}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
                  >
                    {t('admin.users.details.cards.viewTransactions')}
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* API Keys */}
          {user.apiKeys && user.apiKeys.length > 0 && (
            <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
              <h2 className="text-2xl font-heading font-bold text-brand-accent mb-6">
                {t('admin.users.details.apiKeys.title')}
              </h2>
              <div className="space-y-3">
                {user.apiKeys.map((apiKey) => (
                  <div
                    key={apiKey.keyId}
                    className="bg-neutral-dark/50 p-4 rounded-lg border border-neutral-dark"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-neutral-light font-heading font-semibold">
                          {apiKey.label || apiKey.keyId}
                        </p>
                        <p className="text-sm text-neutral-medium font-body mt-1">
                          {t('admin.users.details.apiKeys.created')}:{' '}
                          {formatDate(apiKey.createdAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus')}
                        </p>
                      </div>
                      <span className="text-xs text-neutral-medium font-body font-mono">
                        {apiKey.keyId}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status */}
          <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent mb-6">
              {t('admin.users.details.status.title')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-neutral-medium font-body mb-2 block">
                  {t('admin.users.details.status.accountStatus')}
                </label>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-sm font-body ${
                    user.isBanned
                      ? 'bg-danger/20 text-danger'
                      : 'bg-green-500/20 text-green-500'
                  }`}
                >
                  {user.isBanned
                    ? t('admin.users.status.banned')
                    : t('admin.users.status.active')}
                </span>
              </div>
              <div>
                <label className="text-sm text-neutral-medium font-body mb-2 block">
                  {t('admin.users.details.status.role')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {user.isAdmin && (
                    <span className="inline-block px-3 py-1 rounded-full text-sm font-body bg-brand-accent/20 text-brand-accent">
                      {t('admin.users.status.admin')}
                    </span>
                  )}
                  {user.role === 'admin_support' && (
                    <span className="inline-block px-3 py-1 rounded-full text-sm font-body bg-blue-500/20 text-blue-500">
                      {t('admin.users.status.support')}
                    </span>
                  )}
                  {!user.isAdmin && user.role !== 'admin_support' && (
                    <span className="inline-block px-3 py-1 rounded-full text-sm font-body bg-neutral-dark text-neutral-medium">
                      {t('admin.users.status.regular')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Balances */}
          <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent mb-6">
              {t('admin.users.details.balances.title')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-neutral-medium font-body mb-1 block">
                  {t('admin.users.details.balances.main')}
                </label>
                <p className="text-2xl font-heading font-bold text-brand-accent">
                  {user.balance != null ? parseFloat(user.balance.toString()).toFixed(2) : '0.00'} USD
                </p>
              </div>
              <div>
                <label className="text-sm text-neutral-medium font-body mb-1 block">
                  {t('admin.users.details.balances.referral')}
                </label>
                <p className="text-xl font-heading font-semibold text-neutral-light">
                  {user.referralBalance != null ? parseFloat(user.referralBalance.toString()).toFixed(2) : '0.00'} USD
                </p>
              </div>
              <div>
                <label className="text-sm text-neutral-medium font-body mb-1 block">
                  {t('admin.users.details.balances.cashback')}
                </label>
                <p className="text-xl font-heading font-semibold text-neutral-light">
                  {user.cashbackBalance != null ? parseFloat(user.cashbackBalance.toString()).toFixed(2) : '0.00'} USD
                </p>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent mb-6">
              {t('admin.users.details.stats.title')}
            </h2>
            <div className="space-y-4">
              {user.referralCount !== undefined && (
                <div>
                  <label className="text-sm text-neutral-medium font-body mb-1 block">
                    {t('admin.users.details.stats.referrals')}
                  </label>
                  <p className="text-xl font-heading font-semibold text-neutral-light">
                    {user.referralCount}
                  </p>
                </div>
              )}
              {user.apiKeys && (
                <div>
                  <label className="text-sm text-neutral-medium font-body mb-1 block">
                    {t('admin.users.details.stats.apiKeys')}
                  </label>
                  <p className="text-xl font-heading font-semibold text-neutral-light">
                    {user.apiKeys.length}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Balance Adjustments */}
          <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
              <h2 className="text-2xl font-heading font-bold text-brand-accent mb-6">
                {t('admin.users.details.balanceAdjustments.title')}
              </h2>
              <div className="space-y-3">
                <button
                  onClick={() => setBalanceDialog({ isOpen: true, type: 'main', action: 'add' })}
                  disabled={isAdjustingMain}
                  className="w-full px-4 py-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30 transition-colors text-sm font-body disabled:opacity-50"
                >
                  {t('admin.users.details.balanceAdjustments.addMain')}
                </button>
                <button
                  onClick={() => setBalanceDialog({ isOpen: true, type: 'main', action: 'deduct' })}
                  disabled={isAdjustingMain}
                  className="w-full px-4 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30 transition-colors text-sm font-body disabled:opacity-50"
                >
                  {t('admin.users.details.balanceAdjustments.deductMain')}
                </button>
                <button
                  onClick={() => setBalanceDialog({ isOpen: true, type: 'referral', action: 'add' })}
                  disabled={isAdjustingReferral}
                  className="w-full px-4 py-2 bg-blue-500/20 text-blue-500 rounded-lg hover:bg-blue-500/30 transition-colors text-sm font-body disabled:opacity-50"
                >
                  {t('admin.users.details.balanceAdjustments.addReferral')}
                </button>
                <button
                  onClick={() => setBalanceDialog({ isOpen: true, type: 'referral', action: 'deduct' })}
                  disabled={isAdjustingReferral}
                  className="w-full px-4 py-2 bg-orange-500/20 text-orange-500 rounded-lg hover:bg-orange-500/30 transition-colors text-sm font-body disabled:opacity-50"
                >
                  {t('admin.users.details.balanceAdjustments.deductReferral')}
                </button>
                <button
                  onClick={() => setBalanceDialog({ isOpen: true, type: 'cashback', action: 'add' })}
                  disabled={isAdjustingCashback}
                  className="w-full px-4 py-2 bg-purple-500/20 text-purple-500 rounded-lg hover:bg-purple-500/30 transition-colors text-sm font-body disabled:opacity-50"
                >
                  {t('admin.users.details.balanceAdjustments.addCashback')}
                </button>
                <button
                  onClick={() => setBalanceDialog({ isOpen: true, type: 'cashback', action: 'deduct' })}
                  disabled={isAdjustingCashback}
                  className="w-full px-4 py-2 bg-pink-500/20 text-pink-500 rounded-lg hover:bg-pink-500/30 transition-colors text-sm font-body disabled:opacity-50"
                >
                  {t('admin.users.details.balanceAdjustments.deductCashback')}
                </button>
              </div>
            </div>

          {/* Actions */}
          <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent mb-6">
              {t('admin.users.details.actions.title')}
            </h2>
            <div className="space-y-3">
              <button
                onClick={() => setBanDialog(true)}
                disabled={isTogglingBan}
                className={`w-full px-4 py-2 rounded-lg transition-colors text-sm font-body ${
                  user.isBanned
                    ? 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
                    : 'bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30'
                }`}
              >
                {user.isBanned
                  ? t('admin.users.actions.unban')
                  : t('admin.users.actions.ban')}
              </button>
              <button
                onClick={() => setRoleDialog(true)}
                disabled={isUpdatingRole}
                className={`w-full px-4 py-2 rounded-lg transition-colors text-sm font-body ${
                  user.isAdmin
                    ? 'bg-orange-500/20 text-orange-500 hover:bg-orange-500/30'
                    : 'bg-blue-500/20 text-blue-500 hover:bg-blue-500/30'
                }`}
              >
                {user.isAdmin
                  ? t('admin.users.actions.removeAdmin')
                  : t('admin.users.actions.makeAdmin')}
              </button>
              <button
                onClick={() => setSupportDialog(true)}
                disabled={isUpdatingRole}
                className={`w-full px-4 py-2 rounded-lg transition-colors text-sm font-body ${
                  user.role === 'admin_support'
                    ? 'bg-orange-500/20 text-orange-500 hover:bg-orange-500/30'
                    : 'bg-blue-500/20 text-blue-500 hover:bg-blue-500/30'
                }`}
              >
                {user.role === 'admin_support'
                  ? t('admin.users.actions.removeSupport')
                  : t('admin.users.actions.makeSupport')}
              </button>
              <button
                onClick={() => {
                  setPasswordDialog(true);
                  setPasswordForm({ newPassword: '', confirmPassword: '' });
                }}
                disabled={isChangingPassword}
                className="w-full px-4 py-2 bg-purple-500/20 text-purple-500 rounded-lg hover:bg-purple-500/30 transition-colors text-sm font-body disabled:opacity-50"
              >
                {t('admin.users.actions.changePassword')}
              </button>
              {!user.isAdmin && (
                <button
                  onClick={() => setDeleteDialog(true)}
                  disabled={isDeleting}
                  className="w-full px-4 py-2 bg-danger/20 text-danger rounded-lg hover:bg-danger/30 transition-colors text-sm font-body disabled:opacity-50"
                >
                  {t('admin.users.actions.delete')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Ban/Unban Dialog */}
      <ConfirmDialog
        isOpen={banDialog}
        onClose={() => setBanDialog(false)}
        onConfirm={handleBan}
        title={
          user.isBanned
            ? t('admin.users.unbanTitle')
            : t('admin.users.banTitle')
        }
        message={
          user.isBanned
            ? t('admin.users.unbanConfirm', {
                name: `${user.firstName} ${user.lastName || ''}`,
              })
            : t('admin.users.banConfirm', {
                name: `${user.firstName} ${user.lastName || ''}`,
              })
        }
        confirmText={
          user.isBanned
            ? t('admin.users.actions.unban')
            : t('admin.users.actions.ban')
        }
        type={user.isBanned ? 'info' : 'danger'}
        isLoading={isTogglingBan}
      />

      {/* Role Dialog */}
      <ConfirmDialog
        isOpen={roleDialog}
        onClose={() => setRoleDialog(false)}
        onConfirm={handleRole}
        title={
          user.isAdmin
            ? t('admin.users.removeAdminTitle')
            : t('admin.users.makeAdminTitle')
        }
        message={
          user.isAdmin
            ? t('admin.users.removeAdminConfirm', {
                name: `${user.firstName} ${user.lastName || ''}`,
              })
            : t('admin.users.makeAdminConfirm', {
                name: `${user.firstName} ${user.lastName || ''}`,
              })
        }
        confirmText={
          user.isAdmin
            ? t('admin.users.actions.removeAdmin')
            : t('admin.users.actions.makeAdmin')
        }
        type="info"
        isLoading={isUpdatingRole}
      />

      {/* Support Dialog */}
      <ConfirmDialog
        isOpen={supportDialog}
        onClose={() => setSupportDialog(false)}
        onConfirm={handleSupport}
        title={
          user.role === 'admin_support'
            ? t('admin.users.removeSupportTitle')
            : t('admin.users.makeSupportTitle')
        }
        message={
          user.role === 'admin_support'
            ? t('admin.users.removeSupportConfirm', {
                name: `${user.firstName} ${user.lastName || ''}`,
              })
            : t('admin.users.makeSupportConfirm', {
                name: `${user.firstName} ${user.lastName || ''}`,
              })
        }
        confirmText={
          user.role === 'admin_support'
            ? t('admin.users.actions.removeSupport')
            : t('admin.users.actions.makeSupport')
        }
        type="info"
        isLoading={isUpdatingRole}
      />

      {/* Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t('admin.users.deleteTitle')}
        message={t('admin.users.deleteConfirm', {
          name: `${user.firstName} ${user.lastName || ''}`,
        })}
        confirmText={t('admin.users.actions.delete')}
        type="danger"
        isLoading={isDeleting}
      />

      {/* Change Password Dialog */}
      {passwordDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-neutral-dark/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6 w-full max-w-md mx-4">
            <h3 className="text-2xl font-heading font-bold text-brand-accent mb-4">
              {t('admin.users.password.title')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.users.password.newPassword')} <span className="text-danger">*</span>
                </label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder={t('admin.users.password.newPasswordPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.users.password.confirmPassword')} <span className="text-danger">*</span>
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  placeholder={t('admin.users.password.confirmPasswordPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleChangePassword}
                disabled={isChangingPassword || !passwordForm.newPassword || passwordForm.newPassword.length < 8}
                className="flex-1 px-4 py-2 bg-purple-500/20 text-purple-500 rounded-lg hover:bg-purple-500/30 transition-colors text-sm font-body flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isChangingPassword ? (
                  <>
                    <ButtonSpinner size="xs" />
                    <span>{t('common.loading')}</span>
                  </>
                ) : (
                  t('common.confirm')
                )}
              </button>
              <button
                onClick={() => {
                  setPasswordDialog(false);
                  setPasswordForm({ newPassword: '', confirmPassword: '' });
                }}
                className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-colors text-sm font-body"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Balance Adjustment Dialog */}
      {balanceDialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-neutral-dark/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6 w-full max-w-md mx-4">
            <h3 className="text-2xl font-heading font-bold text-brand-accent mb-4">
              {balanceDialog.action === 'add'
                ? balanceDialog.type === 'main'
                  ? t('admin.users.details.balanceAdjustments.addMainTitle')
                  : balanceDialog.type === 'referral'
                  ? t('admin.users.details.balanceAdjustments.addReferralTitle')
                  : t('admin.users.details.balanceAdjustments.addCashbackTitle')
                : balanceDialog.type === 'main'
                ? t('admin.users.details.balanceAdjustments.deductMainTitle')
                : balanceDialog.type === 'referral'
                ? t('admin.users.details.balanceAdjustments.deductReferralTitle')
                : t('admin.users.details.balanceAdjustments.deductCashbackTitle')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.users.details.balanceAdjustments.amount')} <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  step="any"
                  min="0.01"
                  value={balanceForm.amount}
                  onChange={(e) => setBalanceForm({ ...balanceForm, amount: e.target.value })}
                  placeholder={t('admin.users.details.balanceAdjustments.amountPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.users.details.balanceAdjustments.reason')}
                </label>
                <textarea
                  value={balanceForm.reason}
                  onChange={(e) => setBalanceForm({ ...balanceForm, reason: e.target.value })}
                  placeholder={t('admin.users.details.balanceAdjustments.reasonPlaceholder')}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={async () => {
                  if (!balanceForm.amount) {
                    toast.error(t('admin.users.details.balanceAdjustments.invalidAmount'));
                    return;
                  }

                  const amount = parseFloat(balanceForm.amount);
                  if (isNaN(amount) || amount <= 0) {
                    toast.error(t('admin.users.details.balanceAdjustments.invalidAmount'));
                    return;
                  }

                  try {
                    const type = balanceDialog.action === 'add'
                      ? balanceDialog.type === 'main'
                        ? 'admin_add'
                        : balanceDialog.type === 'referral'
                        ? 'admin_add_referral'
                        : 'admin_add_cashback'
                      : balanceDialog.type === 'main'
                      ? 'admin_deduct'
                      : balanceDialog.type === 'referral'
                      ? 'admin_deduct_referral'
                      : 'admin_deduct_cashback';

                    if (balanceDialog.type === 'main') {
                      await adjustMainBalance({
                        userId: user._id,
                        data: { amount, type: type as 'admin_add' | 'admin_deduct', reason: balanceForm.reason || undefined },
                      }).unwrap();
                    } else if (balanceDialog.type === 'referral') {
                      await adjustReferralBalance({
                        userId: user._id,
                        data: { amount, type: type as 'admin_add_referral' | 'admin_deduct_referral', reason: balanceForm.reason || undefined },
                      }).unwrap();
                    } else {
                      await adjustCashbackBalance({
                        userId: user._id,
                        data: { amount, type: type as 'admin_add_cashback' | 'admin_deduct_cashback', reason: balanceForm.reason || undefined },
                      }).unwrap();
                    }

                    toast.success(t('admin.users.details.balanceAdjustments.success'));
                    setBalanceDialog({ isOpen: false, type: 'main', action: 'add' });
                    setBalanceForm({ amount: '', reason: '' });
                    void refetch();
                  } catch (err) {
                    const error = err as { data?: { message?: string }; message?: string };
                    toast.error(
                      error?.data?.message || error?.message || t('admin.users.details.balanceAdjustments.error')
                    );
                  }
                }}
                disabled={
                  isAdjustingMain || isAdjustingReferral || isAdjustingCashback || !balanceForm.amount || isNaN(parseFloat(balanceForm.amount)) || parseFloat(balanceForm.amount) <= 0
                }
                className={`flex-1 px-4 py-2 rounded-lg transition-colors text-sm font-body flex items-center justify-center gap-2 ${
                  balanceDialog.action === 'add'
                    ? 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
                    : 'bg-red-500/20 text-red-500 hover:bg-red-500/30'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isAdjustingMain || isAdjustingReferral || isAdjustingCashback ? (
                  <>
                    <ButtonSpinner size="xs" />
                    <span>{t('common.loading')}</span>
                  </>
                ) : (
                  t('common.confirm')
                )}
              </button>
              <button
                onClick={() => {
                  setBalanceDialog({ isOpen: false, type: 'main', action: 'add' });
                  setBalanceForm({ amount: '', reason: '' });
                }}
                className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-colors text-sm font-body"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

